'use strict';

var laraInteractiveApi = require('@concord-consortium/lara-interactive-api');
var React = require('react');
var uuid = require('uuid');

const DynamicTextCustomMessageType = "dynamicText";

const wordRegExp = /(((?!_)\w+([-']\w+)?)|([#]))/; // note: the (?!_) is to remove underscores from word character matches
const numberRangeRegExp = /(\d+)\-/;
const findWordAt = (text, startIndex) => {
    const match = wordRegExp.exec(text.substring(startIndex));
    if (match) {
        let word = match[1];
        // numbers are words and we want to return hyphenated words, like "re-test", but each number
        // in a number range like 1-100
        const numberMatch = word.match(numberRangeRegExp);
        if (numberMatch) {
            word = numberMatch[1];
        }
        return { word, index: startIndex + match.index };
    }
};
const findWords = (text) => {
    const words = [];
    let index = 0;
    let word;
    do {
        word = findWordAt(text, index);
        if (word !== undefined) {
            words.push(word);
            index = word.index + word.word.length;
        }
    } while (word !== undefined);
    return words;
};

const params = new URLSearchParams(window.location.search);
let rate = parseFloat(params.get("readAloudRate") || "0.7");
if (isNaN(rate)) {
    rate = 1;
}
const defaultSessionStorage = {
    readAloudEnabled: false
};
class DynamicTextManager {
    constructor(options) {
        this.readAloudEnabled = false;
        this.selectedComponentId = null;
        this.components = {};
        this.currentUtterance = null;
        this.onEvent = options.onEvent;
        this.sessionStorage = defaultSessionStorage;
        try {
            this.sessionStorage = JSON.parse(window.sessionStorage.getItem(DynamicTextManager.SessionStorageKey) || "") || defaultSessionStorage;
        }
        catch (e) {
            // no-op
        }
        this.readAloudEnabled = this.isReadAloudAvailable && this.sessionStorage.readAloudEnabled;
    }
    get isReadAloudAvailable() {
        return !!window.speechSynthesis;
    }
    get isReadAloudEnabled() {
        return this.readAloudEnabled;
    }
    enableReadAloud(options) {
        const { enabled, saveSetting } = options;
        this.readAloudEnabled = enabled;
        this.selectedComponentId = null;
        this.stopSpeaking();
        this.emit({ type: "readAloudEnabled", enabled });
        this.emit({ type: "selected", id: this.selectedComponentId });
        if (saveSetting) {
            try {
                this.sessionStorage.readAloudEnabled = enabled;
                window.sessionStorage.setItem(DynamicTextManager.SessionStorageKey, JSON.stringify(this.sessionStorage));
            }
            catch (e) {
                // no-op
            }
        }
    }
    registerComponent(id, listener) {
        this.components[id] = listener;
        listener({ type: "readAloudEnabled", enabled: this.readAloudEnabled });
    }
    unregisterComponent(id) {
        delete this.components[id];
        if (this.selectedComponentId === id) {
            this.selectedComponentId = null;
            this.stopSpeaking();
        }
    }
    selectComponent(id, options) {
        const text = (options === null || options === void 0 ? void 0 : options.text) || "";
        const readAloud = (options === null || options === void 0 ? void 0 : options.readAloud) || false;
        const extraLoggingInfo = options === null || options === void 0 ? void 0 : options.extraLoggingInfo;
        // nothing to do if not enabled
        if (!this.readAloudEnabled) {
            return;
        }
        if (this.selectedComponentId === id) {
            if (this.currentUtterance) {
                this.onEvent({ type: "readAloudCanceled", text: this.currentUtterance.text, extraLoggingInfo });
            }
            this.selectedComponentId = null;
        }
        else {
            this.selectedComponentId = id;
        }
        if (readAloud) {
            this.stopSpeaking();
        }
        this.emit({ type: "selected", id: this.selectedComponentId });
        if (readAloud && this.selectedComponentId && (text.length > 0)) {
            const wordIndexes = {};
            this.currentUtterance = new SpeechSynthesisUtterance(text);
            this.currentUtterance.rate = rate;
            this.currentUtterance.addEventListener("boundary", (e) => {
                var _a;
                if (this.selectedComponentId && e.name === "word") {
                    // extract the word found at the spoken index
                    const parsedWord = findWordAt(text, e.charIndex);
                    if (parsedWord !== undefined) {
                        const { word } = parsedWord;
                        wordIndexes[word] = (_a = wordIndexes[word]) !== null && _a !== void 0 ? _a : 0;
                        this.emit({ type: "wordUttered", id: this.selectedComponentId, options: { word, wordIndex: wordIndexes[word] } });
                        wordIndexes[word]++;
                    }
                }
            });
            this.currentUtterance.addEventListener("end", () => {
                // mark that this utterance is complete
                this.currentUtterance = null;
                // if this is still the currently selected component deselect it
                if (this.selectedComponentId === id) {
                    this.onEvent({ type: "readAloudComplete", text, extraLoggingInfo });
                    this.selectComponent(null);
                }
            });
            this.emit({ type: "speechStarting", id: this.selectedComponentId });
            window.speechSynthesis.speak(this.currentUtterance);
            this.onEvent({ type: "readAloud", text, extraLoggingInfo });
        }
    }
    stopSpeaking() {
        var _a;
        (_a = window.speechSynthesis) === null || _a === void 0 ? void 0 : _a.cancel();
    }
    emit(message) {
        Object.values(this.components).forEach(listener => listener(message));
    }
}
DynamicTextManager.SessionStorageKey = "dynamicTextSettings";

class DynamicTextProxy {
    constructor() {
        this.listeners = {};
        laraInteractiveApi.getClient().addListener("customMessage", (message) => {
            if (message.type === DynamicTextCustomMessageType) {
                Object.values(this.listeners).forEach(listener => {
                    listener(message.content);
                });
            }
        });
    }
    registerComponent(id, listener) {
        this.listeners[id] = listener;
        this.sendCustomMessage({ type: "register", id });
    }
    unregisterComponent(id) {
        delete this.listeners[id];
        this.sendCustomMessage({ type: "unregister", id });
    }
    selectComponent(id, options) {
        this.sendCustomMessage({ type: "select", id, options });
    }
    sendCustomMessage(message) {
        const customMessage = {
            type: DynamicTextCustomMessageType,
            content: message
        };
        laraInteractiveApi.sendCustomMessage(customMessage);
    }
}
// hook to return singleton instance - this needs to exist on initial render
// so useEffect() can't be used
const proxy = new DynamicTextProxy();
const useDynamicTextProxy = () => proxy;

const DynamicTextContext = React.createContext(null);
DynamicTextContext.displayName = "DynamicTextContext";
const useDynamicTextContext = () => {
    const currentDynamicTextContext = React.useContext(DynamicTextContext);
    if (!currentDynamicTextContext) {
        throw new Error("useDynamicTextContext has to be used within <DynamicTextContext.Provider>");
    }
    return currentDynamicTextContext;
};
// This context is used for containers, like report items, that have dynamic text enabled
// components that are but run in a host (like the reports) which don't support dynamic text.
// It provides empty stub functions and it can also be used in tests
const fakeDynamicTextContext = {
    registerComponent: () => undefined,
    unregisterComponent: () => undefined,
    selectComponent: () => undefined
};
const FakeDynamicTextContext = React.createContext(fakeDynamicTextContext);
const useFakeDynamicTextContext = () => React.useContext(FakeDynamicTextContext);

const addDynamicTextStyles = () => {
    const element = document.createElement("style");
    element.setAttribute("type", "text/css");
    element.dataset.addedBy = "@concord-consortium/dynamic-text";
    // NOTE: the highlight pseudo-class rule to be its own rule as some browsers don't support highlighting and can't process it
    element.textContent = `
    /* this was dynamically added by the @concord-consortium/dynamic-text package */

    .readAloudTextEnabled:hover, .readAloudTextSelected {
      color: black !important;
      background-color: #f8ff00 !important;
      cursor: pointer !important;
    }
    ::highlight(readAloudWordHighlight) {
      color: black !important;
      background-color: #f8ff00 !important;
    }
  `;
    document.getElementsByTagName("head")[0].appendChild(element);
};
addDynamicTextStyles();
// Some browsers don't support highlighting and it is defined in a later ES version than es2018
// which we have set in the tsconfig so we need to cast the window and CSS objects to any in order to reference it
let highlight = undefined;
const Highlight = window.Highlight;
const highlights = CSS.highlights;
if (Highlight && highlights) {
    highlight = new Highlight();
    highlights.set("readAloudWordHighlight", highlight);
}
const DynamicText = ({ noReadAloud, inline, children, context }) => {
    const dynamicText = context || useDynamicTextContext();
    const readAloud = !noReadAloud;
    const ref = React.useRef(null);
    const [id, setId] = React.useState("");
    const [enabled, setEnabled] = React.useState(false);
    const [selected, setSelected] = React.useState(false);
    const [highlightWord, setHighlightWord] = React.useState(undefined);
    const wordInstanceMap = React.useRef({});
    // depth-first walk of the DOM to gather word instances
    const walkDOMToGatherWords = (el) => {
        var _a, _b;
        if (el.nodeType === Node.TEXT_NODE) {
            const words = findWords(el.textContent || "");
            for (const { word, index } of words) {
                wordInstanceMap.current[word] = (_a = wordInstanceMap.current[word]) !== null && _a !== void 0 ? _a : [];
                (_b = wordInstanceMap.current[word]) === null || _b === void 0 ? void 0 : _b.push([el, index]);
            }
        }
        else if (el.hasChildNodes()) {
            el.childNodes.forEach(child => walkDOMToGatherWords(child));
        }
    };
    // register the component onmount and unregister when unmounted
    React.useEffect(() => {
        const componentId = uuid.v4();
        setId(componentId);
        // this will need to change to an api message
        dynamicText.registerComponent(componentId, (message) => {
            // clear highlighting at the start on all messages
            setHighlightWord(undefined);
            switch (message.type) {
                case "readAloudEnabled":
                    setEnabled(message.enabled);
                    break;
                case "selected":
                    setSelected(message.id === componentId);
                    break;
                case "speechStarting":
                    if (message.id === componentId) {
                        wordInstanceMap.current = {};
                        if (ref.current) {
                            walkDOMToGatherWords(ref.current);
                        }
                    }
                    break;
                case "wordUttered":
                    if (message.id === componentId && highlight) {
                        setHighlightWord(message.options);
                    }
                    break;
            }
        });
        return () => dynamicText.unregisterComponent(componentId);
    }, []);
    // highlight the utterance
    React.useEffect(() => {
        var _a;
        // some browsers don't support highlighting
        if (!highlight) {
            return;
        }
        highlight.clear();
        if (highlightWord) {
            const { word, wordIndex } = highlightWord;
            const wordInstance = (_a = wordInstanceMap.current[word]) === null || _a === void 0 ? void 0 : _a[wordIndex];
            if (wordInstance) {
                const range = new Range();
                const [el, index] = wordInstance;
                range.setStart(el, index);
                range.setEnd(el, index + word.length);
                highlight.add(range);
            }
        }
    }, [highlightWord]);
    // select the component when clicked
    const handleClick = React.useCallback((e) => {
        var _a;
        // ignore clicks on links within dynamic text areas and glossary words
        const target = e.target;
        if (((target === null || target === void 0 ? void 0 : target.tagName) === "A") || (target === null || target === void 0 ? void 0 : target.className.includes("GlossaryWord"))) {
            return;
        }
        dynamicText.selectComponent(id, { readAloud, text: (((_a = ref.current) === null || _a === void 0 ? void 0 : _a.innerText) || "").trim() });
    }, [id]);
    // highlighting is active when it is available and a word is being uttered
    const highlighting = !!highlight && !!highlightWord;
    const className = [
        readAloud && enabled && !highlighting ? "readAloudTextEnabled" : "",
        readAloud && selected && !highlighting ? "readAloudTextSelected" : ""
    ].join(" ");
    if (inline) {
        return (React.createElement("span", { className: className, onClick: handleClick, ref: ref }, children));
    }
    return (React.createElement("div", { className: className, onClick: handleClick, ref: ref }, children));
};

exports.DynamicText = DynamicText;
exports.DynamicTextContext = DynamicTextContext;
exports.DynamicTextCustomMessageType = DynamicTextCustomMessageType;
exports.DynamicTextManager = DynamicTextManager;
exports.DynamicTextProxy = DynamicTextProxy;
exports.FakeDynamicTextContext = FakeDynamicTextContext;
exports.fakeDynamicTextContext = fakeDynamicTextContext;
exports.useDynamicTextContext = useDynamicTextContext;
exports.useDynamicTextProxy = useDynamicTextProxy;
exports.useFakeDynamicTextContext = useFakeDynamicTextContext;
//# sourceMappingURL=index.js.map
